<template>
	<div class="chatbot" @load.capture="scrollToBottom()" ref="chatbot">
		<template v-for="element in showed">
			<!-- message -->
			<div
				v-if="element instanceof Message"
				class="chatbot-message"
				:class="[element.fromUser ? 'is-user' : 'is-bot', {'is-warning': element.isWarning}, 'is-' + market ]"
				:data-user-name="translations.chatbot.user_name"
			>
				<template v-if="element.loading">
					<div class="chatbot-writing-dot"></div>
					<div class="chatbot-writing-dot"></div>
					<div class="chatbot-writing-dot"></div>
				</template>
				<span v-else class="content" v-html="element.text"></span>
				<div v-if="element.comeback" class="chatbot-message-comeback">
					<a v-if="awaitingResolver !== null && !stopped" @click="comebackTo(element.comeback)">
						{{ translations.chatbot.change }}
					</a>
					&nbsp;
				</div>
			</div>
			<!-- select -->
			<div
				v-else-if="element instanceof Select"
				class="buttons"
			>
				<a
					v-for="option in element.options"
					class="button is-fullwidth"
					:class="[{'is-complex': option.complex}, option.class]"
					@click="selectOption(option, element)"
					:disabled="element.disabled"
					:href="option.href"
					:download="!!option.href"
				>
					<template v-if="option.complex">
						<div class="complex-content" v-html="option.label"></div>
						<span class="icon">
							<i class="fa-solid fa-arrow-right"></i>
						</span>
					</template>
					<span v-else v-html="option.label"></span>
				</a>
			</div>
			<!-- form -->
			<form
				v-else-if="element instanceof Form"
				@submit.prevent="submitForm(element)"
			>
				<flashes :alerts="alerts.data" v-if="alerts.action === 'chatbot'"/>
				<div class="columns is-multiline">
					<div
						v-for="field in element.fields"
						class="column"
						:class="field.width"
					>
						<div class="field">
							<div class="control label-around-input">
								<label class="label" :for="field.name">
									{{ field.label }}
								</label>
								<div
									v-if="field.select"
									class="select is-fullwidth"
								>
									<select
										:name="field.name"
										:id="element.disabled ? false : field.name"
										v-bind="field.attributes"
										v-model="variables[ field.name ]"
										:disabled="element.disabled"
									>
										<option
											v-for="option in field.options"
											:value="option.value"
										>
											{{ option.label }}
										</option>
									</select>
								</div>
								<input
									v-else-if="field.file"
									class="input"
									type="file"
									:name="field.name"
									:id="element.disabled ? false : field.name"
									:disabled="element.disabled"
									v-bind="field.attributes"
									@change="variables[ field.name ] = $event.target.files"
								/>
								<input
									v-else
									class="input"
									:name="field.name"
									:id="element.disabled ? false : field.name"
									:disabled="element.disabled"
									v-bind="field.attributes"
									v-model="variables[ field.name ]"
								/>
							</div>
							<p v-if="field.error" class="help is-danger" v-html="field.error"></p>
							<p v-if="field.help" class="help" v-html="field.help"></p>
						</div>
					</div>
				</div>
				<div class="buttons">
					<button
						type="submit"
						class="button control is-fullwidth is-primary"
						:disabled="element.disabled"
						:class="{'is-loading': element.loading}"
					>
						{{ element.submitLabel }}
					</button>
				</div>
				<span><!-- Needed for margin presence --></span>
			</form>
			<!-- card -->
			<form
				v-else-if="element instanceof Card"
				@submit.prevent="submitForm(element)"
			>
				<img
					:src="getCardUrl(element)"
					@load="cardLoaded(element)"
					alt="card preview"
				>
				<template v-if="element.editable">
					<div class="field">
						<div class="control label-around-input">
							<label class="label" for="card_name">
								{{ element.label }}
							</label>
							<input
								class="input"
								name="card_name"
								:id="element.disabled ? false : 'card_name'"
								v-model.trim="variables[ element.name ]"
								maxlength="22"
								:class="{'is-danger': isCardUsernameTooLong(variables[ element.name ])}"
								:disabled="element.disabled"
							/>
						</div>
						<p
							v-if="isCardUsernameTooLong(variables[ element.name ])"
							class="help is-danger"
						>
							{{ translations.chatbot.card_name_too_long }}
						</p>
					</div>
					<div class="buttons">
						<button
							type="submit"
							class="button is-fullwidth is-primary"
							:disabled="isCardUsernameTooLong(variables[ element.name ]) || element.disabled"
						>
							{{ element.submitLabel }}
						</button>
					</div>
					<span><!-- Needed so that the .buttons is not a last child so it have a margin --></span>
				</template>
			</form>
			<flashes
				v-else-if="element instanceof Flashes"
				:alerts="element.alerts"
			/>
		</template>
		<delete :display="confirmLeave" :show-close="false" @close="notConfirmedLeave" @confirm="confirmedLeave" :translations="translations.confirmed" />
	</div>
</template>

<script>
	import Message from "./chatbotInstructions/Message";
	import Instruction from "./chatbotInstructions/Instruction";
	import Call from "./chatbotInstructions/Call";
	import Select from "./chatbotInstructions/Select";
	import Form from "./chatbotInstructions/Form";
	import Custom from "./chatbotInstructions/Custom";
	import Card from "./chatbotInstructions/Card";
	import Restart from "./chatbotInstructions/Restart";
	import Flashes from "./chatbotInstructions/Flashes";

	export default {
		props: {
			code: {
				type: Object,
				required: true
			},
			cardUrl: {
				type: String,
			},
			translations: {
				type: Object,
				required: true
			},
			alerts: {
				type: Object,
				required: true,
			},
			preventUnload: {
				type: Boolean,
				default: false
			},
			showChatbot: {
				type: Boolean,
				required: true,
			},
			title: {
				type: String,
				default: ''
			},
			market: {
				type: String,
				default: 'monizze'
			}
		},
		data() {
			return {
				currentInstructionPointer: {
					function: 'main',
					instruction: 0,
				},
				callStack: [],
				showed: [],
				awaitingResolver: null,
				stopped: false,
				variables: {},
				confirmLeave: false,

				Instruction,
				Message,
				Select,
				Form,
				Card,
				Flashes
			};
		},
		methods: {
			async start()
			{
				while (!this.stopped)
				{
					await this.executeNextInstruction();
				}
			},
			/**
			 * @returns {Instruction}
			 */
			currentInstruction()
			{
				return this.code[ this.currentInstructionPointer.function ][ this.currentInstructionPointer.instruction ];
			},
			async executeNextInstruction()
			{
				const self = this;

				// Get the current instruction, when moving out of a function pop back to the previous entry in the stack
				let instruction = this.currentInstruction();

				while(!instruction && this.callStack.length > 0)
				{
					this.currentInstructionPointer = this.callStack.pop();
					instruction = this.currentInstruction();
				}

				// If we can't find any more instruction to execute we stop our self, nothing more to do
				if ( !instruction )
				{
					this.stopped = true;
					return;
				}

				self.currentInstructionPointer.instruction++;

				instruction = _.cloneDeep(instruction);

				// We resolve the instruction for the current values of the variables
				instruction.resolve(this.variables);

				// If the instruction sould be skip, we have nothing more to do
				if(!instruction.condition)
				{
					return;
				}

				/*
				 * Show a message (as bot or user)
				 */
				if (instruction instanceof Message)
				{
					if( instruction.comeback )
					{
						// If the message alow to comeback to it we need to store the current function
						// so that we can call it back when needed
						instruction.comeback = this.currentInstructionPointer.function;
					}

					self.showed.push(instruction);
					this.scrollToBottom();

					await instruction.show();

					this.scrollToBottom();
				}
				else if (instruction instanceof Flashes)
				{
					self.showed.push(instruction);

					this.scrollToBottom();
				}
				/*
				 * Interaction with the user
				 */
				else if(
					instruction instanceof Select
					|| instruction instanceof Form
					|| instruction instanceof Card
				)
				{
					self.showed.push(instruction);
					this.scrollToBottom();

					return new Promise(
						function(resolve)
						{
							self.awaitingResolver = resolve;
						}
					);
				}
				/*
				 * Call an other function
				 */
				else if(instruction instanceof Call)
				{
					// If ther is no functionName we ignore the instruction (usefull for conditional call)
					if( !instruction.functionName )
					{
						return;
					}

					// Save the next instruction so we can come back to it after the called function
					this.callStack.push(this.currentInstructionPointer);

					// Jump to the start of the selected function
					this.currentInstructionPointer = {
						function: instruction.functionName,
						instruction: 0,
					};
				}
				/*
				 * Execute custom js code on variables
				 */
				else if(instruction instanceof Custom)
				{
					// Show a loading message
					this.showed.push(new Message());
					this.scrollToBottom();

					try {
						await instruction.callback(this.variables);

						this.showed.pop();
					}
					catch (e) {
						this.showed.pop();

						if( typeof Sentry !== 'undefined' )
						{
							Sentry.captureException(e);
						}
						else
						{
							console.error(e); // No Sentry = dev
						}

						this.variables.errors = _.cloneDeep(this.alerts.data);
						this.variables.errorsFrom = this.currentInstructionPointer.function;

						// If an error function for the current function exist we jump to it
						if( this.code[ this.currentInstructionPointer.function + '_error' ] )
						{
							this.currentInstructionPointer = {
								function: this.currentInstructionPointer.function + '_error',
								instruction: 0,
							};
						}
						// Else, if a global error function exist we jump to it
						else if( this.code[ 'error' ] )
						{
							this.currentInstructionPointer = {
								function: 'error',
								instruction: 0,
							};
						}
						// Else we just show the errors and stop here
						else
						{
							this.showed.push(new Flashes(this.variables.errors));
							this.stopped = true;
						}
					}
				}
				/*
				 * Restart the system, resetting all state and beginning from a selected function
				*/
				else if(instruction instanceof Restart)
				{
					this.currentInstructionPointer = {
						function: instruction.functionName || 'main',
						instruction: 0,
					};
					this.callStack = [];
					this.showed = [];
					this.awaitingResolver = null;
					this.stopped = false;
					this.variables = {};
				}
				else
				{
					console.error('Unknow instruction', instruction);
					this.stopped = true;
				}
			},
			selectOption(option, element)
			{
				if( element.disabled )
				{
					return;
				}

				this.showed.pop();

				this.variables[ element.name ] = option.value;

				this.awaitingResolver();
				this.awaitingResolver = null;
			},
			async submitForm(/** Form */ element)
			{
				element.loading = true;

				try
				{
					if (element.validation)
					{
						await element.validation(this.variables);
					}
				}
				catch (e)
				{
					element.loading = false;
					return;
				}

				this.showed.pop();

				this.awaitingResolver();
				this.awaitingResolver = null;
			},
			comebackTo(functionName)
			{
				this.callStack.push({
					function: this.currentInstructionPointer.function,
					instruction: 0,
				});

				this.currentInstructionPointer = {
					function: functionName,
					instruction: 0,
				};

				this.showed[ this.showed.length - 1 ].disabled = true;

				if( this.awaitingResolver !== null )
				{
					this.awaitingResolver();
				}
			},
			getCardUrl(element)
			{
				const name = element.editable ? this.variables[element.name] : element.value;

				return this.cardUrl
					+ '&name=' + encodeURIComponent(name)
					+ '&card_number=5065 0000 0000 0000'
					+ '&exp_date=' + moment().add(5, 'y').format('YYYY-MM-DD');
			},
			cardLoaded(element)
			{
				if ( !element.editable )
				{
					this.awaitingResolver();
					this.awaitingResolver = null;
				}
			},
			isCardUsernameTooLong(cardUsername)
			{
				return cardUsername.length > 22;
			},
			/**
			 * Compute how long bouncing dots are displayed, based on
			 * text length
			 */
			calculateTypingTime(text)
			{
				const cps = 100;
				let time = Math.round((text.length / cps) * 1000);

				return Math.min(time, 2500);
			},
			scrollToBottom()
			{
				const target = this.$refs.chatbot;

				if (!target)
				{
					return;
				}

				setTimeout(
					function()
					{
						target.scrollIntoView({
							block: 'end',
							behavior: 'smooth',
						});
					},
					20
				);
			},
			/**
			 * Function to prevent use to quit the process of employee creation in the middle of unsaved information
			 */
			preventUnloadInCreation (e)
			{
				if ( this.preventUnload )
				{
					// Cancel the default event
					e.preventDefault();
					//needed for Chrome
					e.returnValue = '';
				}
			},
			removePreventUnloadInCreation()
			{
				window.removeEventListener('beforeunload', this.preventUnloadInCreation);
			},
			confirmedLeave()
			{
				this.removePreventUnloadInCreation();
				this.$emit('close');
			},
			notConfirmedLeave()
			{
				// nothing to do handle except to hide confirmation
				// we don't want to leave ;)
				this.confirmLeave = false;
			},
			confirmCloseChatbot()
			{
				if (!this.preventUnload)
				{
					this.confirmedLeave();
					return;
				}
				this.confirmLeave = true
			}
		},
		mounted()
		{
			window.addEventListener('beforeunload', this.preventUnloadInCreation);
			this.start();
		},
		beforeDestroy()
		{
			this.removePreventUnloadInCreation();
		}
	}
</script>
